import { ShimmerTwoCol } from "./DashboardShimmer";
import "./dashboard-sections.css";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { TOTAL_RIPENING_ROOM_CAPACITY, RIPENING_ROOM_CHART_TICK_INTERVAL } from "../../../helpers/constants";

// ── Dummy data ───────────────────────────────────────────────────────────────
const COLD_DUMMY = {
  total_pallets:         148,
  total_cases:          4320,
  total_fruit_entered: 89600,
};

const RIPENING_DUMMY = {
  total_capacity:      500,
  percentage_occupied: 72,
  total_cases:         360,
  total_weight:      18400,
  rooms: [
    { name: "Room 1", capacity: TOTAL_RIPENING_ROOM_CAPACITY, filled: 180 },
    { name: "Room 2", capacity: TOTAL_RIPENING_ROOM_CAPACITY, filled: 390 },
    { name: "Room 3", capacity: TOTAL_RIPENING_ROOM_CAPACITY, filled: 280 },
    { name: "Room 4", capacity: TOTAL_RIPENING_ROOM_CAPACITY, filled: 168 },
    { name: "Room 5", capacity: TOTAL_RIPENING_ROOM_CAPACITY, filled: 0   },
    { name: "Room 6", capacity: TOTAL_RIPENING_ROOM_CAPACITY, filled: 150 },
    { name: "Room 7", capacity: TOTAL_RIPENING_ROOM_CAPACITY, filled: 260 },
    { name: "Room 8", capacity: TOTAL_RIPENING_ROOM_CAPACITY, filled: 400 },
    { name: "Room 9", capacity: TOTAL_RIPENING_ROOM_CAPACITY, filled: 290 },
  ],
};

// ── Occupancy ring ───────────────────────────────────────────────────────────
const OccupancyRing = ({ pct = 72 }) => {
  const r = 36;
  const circ = 2 * Math.PI * r;
  const filled = (pct / 100) * circ;
  return (
    <div className="db-ring-wrapper">
      <svg width="90" height="90" viewBox="0 0 90 90">
        <circle cx="45" cy="45" r={r} fill="none" stroke="#f0f1f3" strokeWidth="8" />
        <circle
          cx="45" cy="45" r={r}
          fill="none"
          stroke="#ee7225"
          strokeWidth="8"
          strokeDasharray={`${filled} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 45 45)"
        />
      </svg>
      <span className="db-ring-label">{pct}%</span>
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────────────
const ColdRoomDataOverview = ({
  coldData     = COLD_DUMMY,
  ripeningData = RIPENING_DUMMY,
  isLoading    = false,
}) => {
  const pctNum = ripeningData.percentage_occupied || 0;

  const barOptions = {
    chart: { type: "column", height: 200, backgroundColor: "transparent" },
    title: { text: null },
    credits: { enabled: false },
    xAxis: {
      categories: ripeningData.rooms.map((r) => r.name),
      labels: { style: { fontSize: "10px", color: "#8c97a7" } },
    },
    yAxis: {
      title: { text: null },
      gridLineColor: "#f0f1f3",
      tickInterval: RIPENING_ROOM_CHART_TICK_INTERVAL,
      max: TOTAL_RIPENING_ROOM_CAPACITY + RIPENING_ROOM_CHART_TICK_INTERVAL,
      labels: { style: { fontSize: "10px", color: "#8c97a7" } },
    },
    legend: {
      align: "center",
      verticalAlign: "top",
      layout: "horizontal",
      itemStyle: { fontSize: "10px", color: "#667085", fontWeight: "500" },
    },
    tooltip: { shared: true },
    plotOptions: {
      column: { grouping: true, borderWidth: 0, borderRadius: 3, dataLabels: { enabled: false } },
    },
    series: [
      {
        name: "Total Capacity",
        data: ripeningData.rooms.map((r) => r.capacity),
        color: "#ee7225",
      },
      {
        name: "Cases Filled",
        data: ripeningData.rooms.map((r) => r.filled),
        color: "#25aa91",
      },
    ],
  };

  return (
    <div className="db-section">
      <div className="db-section-title">
        <span className="db-title-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2v20M2 12h20M4.93 4.93l14.14 14.14M19.07 4.93L4.93 19.07"/>
          </svg>
        </span>
        Cold + Room Data Overview
      </div>

      {isLoading ? (
        <ShimmerTwoCol />
      ) : (
        <div className="db-two-col">

          {/* ── LEFT: Ripening Room — ring + stats + bar chart ── */}
          <div className="db-avg-card">
            <div className="db-avg-header">
              <span className="db-avg-header-title">Ripening Room</span>
              <span className="db-avg-header-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              </span>
            </div>

            {/* Ring + stat rows */}
            <div className="db-ripening-body">
              <div className="db-ripening-ring-col">
                <OccupancyRing pct={pctNum} />
                <span className="db-ring-sublabel">Occupied</span>
              </div>
              <div className="db-ripening-stats-col">
                <div className="db-avg-row">
                  <span>Total Capacity</span>
                  <span className="db-avg-val">{ripeningData.total_capacity?.toLocaleString()} <span className="db-metric-unit">cases</span></span>
                </div>
                <div className="db-avg-row">
                  <span>Total Cases</span>
                  <span className="db-avg-val">{ripeningData.total_cases?.toLocaleString()}</span>
                </div>
                <div className="db-avg-row">
                  <span>Total Weight</span>
                  <span className="db-avg-val">{ripeningData.total_weight?.toLocaleString()} <span className="db-metric-unit">kg</span></span>
                </div>
                <div className="db-avg-row">
                  <span>% Occupied</span>
                  <span className="db-avg-val db-highlight">{ripeningData.percentage_occupied}%</span>
                </div>
              </div>
            </div>

            {/* Bar chart — per room capacity vs filled */}
            <div style={{ borderTop: "1px solid #f0f1f3", padding: "4px 12px 8px" }}>
              <HighchartsReact highcharts={Highcharts} options={barOptions} />
            </div>
          </div>

          {/* ── RIGHT: Cold Storage — 3 metric boxes only ── */}
          <div className="db-avg-card">
            <div className="db-avg-header">
              <span className="db-avg-header-title">Cold Storage</span>
              <span className="db-avg-header-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2v20M2 12h20M4.93 4.93l14.14 14.14M19.07 4.93L4.93 19.07"/>
                </svg>
              </span>
            </div>
            <div className="db-inner-metric-row">
              <div className="db-inner-metric">
                <div className="db-metric-label">Total Pallets</div>
                <div className="db-metric-value">{coldData.total_pallets?.toLocaleString()}</div>
              </div>
              <div className="db-inner-metric">
                <div className="db-metric-label">Total Cases</div>
                <div className="db-metric-value">{coldData.total_cases?.toLocaleString()}</div>
              </div>
              <div className="db-inner-metric">
                <div className="db-metric-label">Fruit Entered</div>
                <div className="db-metric-value">
                  {coldData.total_fruit_entered?.toLocaleString()}
                  <span className="db-metric-unit"> kg</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default ColdRoomDataOverview;
